/**
 * Scoring Rubric Modal
 * Fullscreen modal explaining how Smart Prompt scores AI responses
 * Based on smart_prompt_user_facing_scoring_rubric_plain_english.md
 */

import React from 'react';

const scoringCriteria = [
  {
    icon: '🏆',
    title: 'Overall',
    description: 'Composite score that blends every category using your selected weights.',
  },
  {
    icon: '🎯',
    title: 'Accuracy',
    description: 'Facts, math, and claims are correct and consistent.',
  },
  {
    icon: '✅',
    title: 'Completeness',
    description: 'Answers every required part in the requested format.',
  },
  {
    icon: '📘',
    title: 'Clarity',
    description: 'Easy to follow; clear structure and labeling.',
  },
  {
    icon: '🔍',
    title: 'Relevance',
    description: 'Stays on-topic; follows your instructions and constraints.',
  },
  {
    icon: '💡',
    title: 'Creativity',
    description: 'Useful, original ideas—no boilerplate or fluff.',
  },
  {
    icon: '🛡️',
    title: 'Bias & Safety',
    description: 'Responsible tone; avoids unsafe or policy-breaking content.',
  },
  {
    icon: '✂️',
    title: 'Conciseness',
    description: 'Gets to the point; just enough detail to be useful.',
  },
];

interface ScoringRubricModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ScoringRubricModal: React.FC<ScoringRubricModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const modalTopPadding = 'calc(var(--main-nav-height, 4.5rem) + 1.5rem)';

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen px-4 pb-8" style={{ paddingTop: modalTopPadding }}>
        <div
          className="max-w-4xl mx-auto bg-slate-900 rounded-xl shadow-2xl border border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between rounded-t-xl z-10">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span>✨</span>
              <span>How We Score AI Responses</span>
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors text-2xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Introduction */}
            <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-cyan-500">
              <p className="text-slate-300 text-sm leading-relaxed">
                A visual overview of how we score AI responses in Smart Prompt.
                Keep this as your reference when comparing results.
              </p>
            </div>

            {/* The 7 Criteria */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🔷</span>
                The 7 Things We Score
              </h3>

              <div className="bg-slate-800/30 rounded-lg border border-slate-700 overflow-hidden">
                <div className="hidden md:block">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Icon</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">What it means</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {scoringCriteria.map((criterion) => (
                        <tr key={criterion.title} className="hover:bg-slate-800/30">
                          <td className="px-4 py-3 text-2xl">{criterion.icon}</td>
                          <td className="px-4 py-3 font-semibold text-white">{criterion.title}</td>
                          <td className="px-4 py-3 text-slate-300 text-sm">{criterion.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden divide-y divide-slate-700">
                  {scoringCriteria.map((criterion) => (
                    <div key={criterion.title} className="p-4 flex gap-3">
                      <div className="text-2xl flex-shrink-0">{criterion.icon}</div>
                      <div>
                        <div className="font-semibold text-white">{criterion.title}</div>
                        <p className="text-slate-300 text-sm">{criterion.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
                <p className="text-sm text-cyan-200">
                  <strong>Score scale:</strong> 0.0 → 10.0 (decimal precision) <span className="mx-2">•</span>
                  <strong>Anchors:</strong> <code className="bg-slate-800 px-2 py-0.5 rounded">9.0–10.0 = Excellent</code> ·
                  <code className="bg-slate-800 px-2 py-0.5 rounded">7.0–8.9 = Good</code> ·
                  <code className="bg-slate-800 px-2 py-0.5 rounded">5.0–6.9 = Average</code> ·
                  <code className="bg-slate-800 px-2 py-0.5 rounded">3.0–4.9 = Below Average</code> ·
                  <code className="bg-slate-800 px-2 py-0.5 rounded">0.0–2.9 = Poor</code>
                </p>
                <p className="text-xs text-cyan-300/80 mt-2">
                  💡 Judges use decimal scores (e.g., 7.3, 8.7, 6.2) for granular differentiation between responses.
                </p>
              </div>
            </section>

            {/* How We Produce Scores */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🧭</span>
                How We Produce the Scores
              </h3>

              <div className="space-y-3">
                {[
                  { num: '1️⃣', title: 'Many judges, not one', desc: 'Up to 3 judges from the comparison are randomly selected to judge all the responses (including their own).' },
                  { num: '2️⃣', title: 'Blind & shuffled', desc: "Judges don't see who wrote which answer, and we randomize order to reduce position bias." },
                  { num: '3️⃣', title: 'Final roll-up', desc: 'We combine per-category results into a composite score.' }
                ].map((step, i) => (
                  <div key={i} className="flex gap-3 bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                    <span className="text-2xl flex-shrink-0">{step.num}</span>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                      <p className="text-sm text-slate-400">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Why This Is Fair */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🧩</span>
                Why This Is Fair
              </h3>

              <ul className="space-y-2">
                {[
                  'Blind & shuffled judging removes brand and position bias.',
                  'Multiple judges dilute any one model\'s preferences.',
                  'Judges are randomly chosen from the models you used, spreading scoring power instead of letting one model dominate the scores.'
                ].map((point, i) => (
                  <li key={i} className="flex gap-3 text-slate-300 text-sm">
                    <span className="text-green-400 flex-shrink-0">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* FAQ */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">❓</span>
                Frequently Asked Questions
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">Can a model "game" its own score?</h4>
                  <p className="text-sm text-slate-400">
                    Not easily—having multiple judges who evaluate all responses keeps the scoring fair and balanced.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};
